import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Reorder, useDragControls } from "framer-motion";
import { Copy, Check, Pencil, PlugZap, Eye, Maximize2, Minimize2, Columns3, CheckCircle2, CircleSlash, GripVertical, RefreshCw, ChevronUp, ChevronDown, ChevronsUpDown, AlertTriangle, Ticket, Flag } from "lucide-react";
import { useLang } from "@/i18n";
import { timeAgo, fmtDate, fmtDateOnly, fmtAuto } from "@/lib/time";
import { loadPlacement, savePlacement, loadOrder, saveOrder, placementOf, inTable, applyOrder, fieldLabel, tvStatusOf, LABELS } from "@/lib/fieldPrefs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import TaskFlagDialog from "@/components/TaskFlagDialog";
import api, { apiError } from "@/lib/api";

const tvClean = (id) => (id || "").replace(/\s+/g, "");
const dash = <span className="text-gray-600">—</span>;

function sortValue(d, key) {
  if (key === "actions") return d.task_flag ? 0 : 1;
  if (key === "agent") return d.agent_installed ? 1 : 0;
  if (key === "payload_version") return typeof d.payload_version === "number" ? d.payload_version : -1;
  if (key === "first_seen" || key === "last_seen") return d[key] ? new Date(d[key]).getTime() : 0;
  if (key === "group") return (d.effective_group || "").toLowerCase();
  if (key.startsWith("extra.")) return String((d.extra || {})[key.slice(6)] ?? "").toLowerCase();
  return String(d[key] ?? "").toLowerCase();
}

function cmpVals(a, b) {
  if (typeof a === "number" && typeof b === "number") return a - b;
  return String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: "base" });
}

function Led({ status }) {
  if (status === "online")
    return (
      <span data-testid="led-online" title="Online"
        className="inline-block w-3 h-3 rounded-full bg-green-400 shadow-[0_0_8px_2px_rgba(16,185,129,0.7)] pulse-dot" />
    );
  if (status === "offline")
    return (
      <span data-testid="led-offline" title="Offline"
        className="inline-block w-3 h-3 rounded-full bg-red-500 shadow-[0_0_8px_2px_rgba(239,68,68,0.6)]" />
    );
  if (status === "tv_reachable")
    return (
      <span data-testid="led-tv_reachable" title="Erreichbar über TeamViewer — Agent meldet gerade nicht"
        className="inline-block w-3 h-3 rounded-full bg-amber-400 shadow-[0_0_8px_2px_rgba(251,191,36,0.7)] pulse-dot" />
    );
  return (
    <span data-testid="led-no_agent" title="Kein Agent"
      className="inline-block w-3 h-3 rounded-full border-2 border-gray-600 bg-transparent" />
  );
}

function AgentCell({ installed }) {
  const { t } = useLang();
  return installed ? (
    <span data-testid="agent-yes" title={t("installed")} className="inline-flex items-center gap-1.5 text-xs font-mono text-gray-800">
      <CheckCircle2 className="w-4 h-4" />
    </span>
  ) : (
    <span data-testid="agent-no" title={t("not_installed")} className="inline-flex items-center gap-1.5 text-xs font-mono text-gray-600">
      <CircleSlash className="w-4 h-4" />
    </span>
  );
}

function UidCell({ value }) {
  const [open, setOpen] = useState(false);
  const [done, setDone] = useState(false);
  if (!value) return dash;
  const short = value.length > 16 ? `${value.slice(0, 8)}…${value.slice(-4)}` : value;
  const copy = (e) => {
    e.stopPropagation();
    navigator.clipboard.writeText(value);
    setDone(true);
    toast.success(value);
    setTimeout(() => setDone(false), 1400);
  };
  return (
    <div className="flex items-center gap-1.5" onClick={(e) => e.stopPropagation()}>
      <button
        data-testid={`uid-toggle-${value}`}
        onClick={() => setOpen((o) => !o)}
        title={value}
        className="inline-flex items-center gap-1.5 font-mono text-sm text-gray-700 hover:text-gray-800 transition-colors whitespace-nowrap"
      >
        <span>{open ? value : short}</span>
        {open ? <Minimize2 className="w-3.5 h-3.5 shrink-0" /> : <Maximize2 className="w-3.5 h-3.5 shrink-0 opacity-60" />}
      </button>
      <button
        data-testid={`uid-copy-${value}`}
        onClick={copy}
        className="text-gray-600 hover:text-gray-800 transition-colors"
        title="Copy"
      >
        {done ? <Check className="w-3.5 h-3.5 text-gray-800" /> : <Copy className="w-3.5 h-3.5" />}
      </button>
    </div>
  );
}

function TeamViewerCell({ id, uid, status, changedAt, prevId }) {
  const { t } = useLang();
  const clean = tvClean(id);
  if (!clean) return dash;
  const copy = (e) => {
    e.stopPropagation();
    navigator.clipboard.writeText(clean);
    toast.success(clean);
  };
  const cls =
    status === "attention"
      ? "text-amber-700 bg-amber-500/10 border-amber-500/40 hover:bg-amber-500/20"
      : status === "offline"
      ? "text-red-700 bg-red-500/10 border-red-500/40 hover:bg-red-500/20"
      : status === "unknown"
      ? "text-amber-700 bg-amber-500/10 border-amber-500/40 hover:bg-amber-500/20"
      : "text-green-700 bg-green-500/10 border-green-500/40 hover:bg-green-500/20";
  const stLabel = status === "attention" ? "nicht im TeamViewer-Konto — manueller Eingriff" : status === "offline" ? t("offline") : status === "unknown" ? "unbekannt (kein Live-Status)" : t("online");
  return (
    <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
      <a
        data-testid={`tv-connect-${uid}`}
        href={`teamviewer10://control?device=${clean}`}
        title={`${t("connect")} · ${stLabel} · ${clean}`}
        className={`inline-flex items-center gap-1.5 font-mono text-sm border rounded-md px-2 py-1 transition-colors ${cls}`}
      >
        <span className={`w-1.5 h-1.5 rounded-full ${status === "attention" ? "bg-amber-400 pulse-dot" : status === "offline" ? "bg-red-500" : status === "unknown" ? "bg-amber-400" : "bg-emerald-400 pulse-dot"}`} />
        <PlugZap className="w-3.5 h-3.5" />
        {clean}
      </a>
      {changedAt && prevId && tvClean(prevId) !== clean && (
        <span
          data-testid={`tv-changed-${uid}`}
          title={`${t("tv_changed")} · ${fmtDate(changedAt)}`}
          className="inline-flex items-center gap-1 text-[10px] font-mono px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-700 border border-amber-500/30"
        >
          <RefreshCw className="w-3 h-3" /> {t("tv_prev")}: <span className="line-through opacity-80">{prevId}</span>
        </span>
      )}
      <button
        data-testid={`tv-copy-${uid}`}
        onClick={copy}
        className="text-gray-600 hover:text-gray-800 transition-colors"
        title="Copy"
      >
        <Copy className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}

function InternetLed({ value }) {
  const v = (value || "").toString().trim().toLowerCase();
  if (!v) return dash;
  const online = ["online", "true", "1", "ok", "connected", "yes", "ja"].includes(v);
  return (
    <span
      data-testid="internet-led"
      title={online ? "Internet: online" : "Internet: offline"}
      className={`inline-block w-3 h-3 rounded-full ${
        online
          ? "bg-emerald-500 shadow-[0_0_8px_2px_rgba(16,185,129,0.7)] pulse-dot"
          : "bg-red-500 shadow-[0_0_8px_2px_rgba(239,68,68,0.6)]"
      }`}
    />
  );
}

function ScannerLed({ value }) {
  const v = (value || "").toString().trim().toLowerCase();
  if (!v) return dash;
  const on = ["ja", "yes", "true", "1", "connected", "verbunden"].includes(v);
  return (
    <span
      data-testid="scanner-led"
      title={on ? "Scanner verbunden" : "Scanner getrennt"}
      className={`inline-block w-3 h-3 rounded-full ${on ? "bg-emerald-500 shadow-[0_0_8px_2px_rgba(16,185,129,0.7)]" : "bg-red-500 shadow-[0_0_8px_2px_rgba(239,68,68,0.7)]"}`}
    />
  );
}


function textCell(v) {
  return v ? <span className="text-sm text-gray-700">{v}</span> : dash;
}

export default function DeviceTable({ devices, onOpen, onChanged, lang, selectedCodes, onToggleSelect, onToggleAll, dense }) {
  const { t } = useLang();
  const has = (c) => !!(selectedCodes && selectedCodes.has(c));
  const rowPad = dense ? "px-2 py-1.5" : "px-2 py-3";
  const allSelected = devices.length > 0 && devices.every((d) => has(d.device_code));
  const [editing, setEditing] = useState(null);
  const [editValue, setEditValue] = useState("");
  const [flash, setFlash] = useState({});
  const [colMenu, setColMenu] = useState(false);
  const [placement, setPlacement] = useState(loadPlacement);
  const [order, setOrder] = useState(loadOrder);
  const [flagDev, setFlagDev] = useState(null);
  const prevRef = useRef({});
  const navigate = useNavigate();
  const [ticketsByLoc, setTicketsByLoc] = useState({});

  useEffect(() => {
    let active = true;
    const fetchTickets = async () => {
      try {
        const r = await api.get("/tickets", { params: { status: "open" } });
        if (!active) return;
        const m = {};
        (r.data || []).forEach((tk) => { m[tk.location_id] = (m[tk.location_id] || 0) + 1; });
        setTicketsByLoc(m);
      } catch { /* ignoriert */ }
    };
    fetchTickets();
    const h = () => fetchTickets();
    window.addEventListener("tickets:changed", h);
    return () => { active = false; window.removeEventListener("tickets:changed", h); };
  }, []);

  const ticketBadge = (lid) => {
    const n = ticketsByLoc[lid];
    if (!n) return null;
    return (
      <button
        data-testid={`loc-ticket-badge-${lid}`}
        onClick={(e) => { e.stopPropagation(); navigate(`/locations/${encodeURIComponent(lid)}`); }}
        title={n + " offene Tickets"}
        className="inline-flex items-center gap-0.5 font-mono text-[10px] font-bold text-amber-700 bg-amber-500/15 border border-amber-500/30 rounded px-1 py-0.5 hover:bg-amber-500/25 transition-colors"
      >
        <Ticket className="w-2.5 h-2.5" /> {n}
      </button>
    );
  };

  const setPlace = (key, mode) => {
    setPlacement((prev) => {
      const next = { ...prev, [key]: mode };
      savePlacement(next);
      return next;
    });
  };

  const reorder = (newKeys) => { setOrder(newKeys); saveOrder(newKeys); };
  const moveKey = (key, dir) => {
    const arr = [...orderedKeys];
    const i = arr.indexOf(key), j = i + dir;
    if (i < 0 || j < 0 || j >= arr.length) return;
    [arr[i], arr[j]] = [arr[j], arr[i]];
    setOrder(arr); saveOrder(arr);
  };

  useEffect(() => {
    const prev = prevRef.current;
    const newFlash = {};
    devices.forEach((d) => {
      const sig = JSON.stringify([d.hostname, d.teamviewer_id, d.tablet_serial, d.manufacturer, d.model, d.device_code, d.location_name, d.status, d.agent_installed, d.extra]);
      if (prev[d.device_code] && sig !== prev[d.device_code]) newFlash[d.device_code] = Date.now();
      prevRef.current[d.device_code] = sig;
    });
    if (Object.keys(newFlash).length) {
      setFlash((f) => ({ ...f, ...newFlash }));
      setTimeout(() => setFlash({}), 1700);
    }
  }, [devices]);

  const startEdit = (d, e) => {
    e.stopPropagation();
    setEditing(d.device_code);
    setEditValue(d.device_code || "");
  };

  const saveEdit = async (id, origCode, e) => {
    e && e.stopPropagation();
    try {
      await api.patch(`/devices/${encodeURIComponent(id)}`, { device_code: editValue });
      toast.success(lang === "de" ? "Geräte-Code gespeichert" : "Device code saved");
      setEditing(null);
      onChanged && onChanged();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const renderCode = (d) =>
    editing === d.device_code ? (
      <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
        <input
          data-testid={`edit-code-input-${d.device_code}`}
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && saveEdit(d.device_code, d.device_code, e)}
          autoFocus
          className="w-28 bg-white border border-[#c00000] rounded px-2 py-1 text-sm font-mono text-gray-900 outline-none"
        />
        <button
          data-testid={`save-code-${d.device_code}`}
          onClick={(e) => saveEdit(d.device_code, d.device_code, e)}
          className="p-1 text-gray-800 hover:bg-gray-100 rounded"
        >
          <Check className="w-4 h-4" />
        </button>
      </div>
    ) : (
      <div className="flex items-center gap-1.5">
      <button
        data-testid={`code-badge-${d.device_code}`}
        onClick={(e) => startEdit(d, e)}
        className="inline-flex items-center gap-1.5 font-mono text-sm font-semibold text-gray-800 bg-gray-100 border border-[#c00000]/20 rounded px-2 py-1 hover:border-[#c00000]/50 group transition-colors"
      >
        {d.device_code || "—"}
        <Pencil className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" />
      </button>
      {(d.device_code || "").includes("?") && (
        <span data-testid={`unclear-badge-${d.device_code}`} title={t("unclear_code") || "Unklarer Gerätecode – bitte korrigieren"} className="inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-wide text-amber-700 bg-amber-500/15 border border-amber-500/30 rounded px-1.5 py-0.5">
          <AlertTriangle className="w-3 h-3" /> ?
        </span>
      )}
      {d.closes_on && d.closes_on <= new Date().toISOString().slice(0, 10) && (
        <span data-testid={`closed-badge-${d.device_code}`} title={`closed ${d.closes_on}`} className="font-mono text-[10px] uppercase tracking-wide text-red-700 bg-red-500/15 border border-red-500/30 rounded px-1.5 py-0.5">closed</span>
      )}
      {d.closes_on && d.closes_on > new Date().toISOString().slice(0, 10) && (
        <span data-testid={`closing-badge-${d.device_code}`} title={fmtDateOnly(d.closes_on)} className="font-mono text-[10px] text-amber-700 bg-amber-500/15 border border-amber-500/30 rounded px-1.5 py-0.5">{fmtDateOnly(d.closes_on)}</span>
      )}
      {d.task_flag && (
        <span data-testid={`task-badge-${d.device_code}`} title={d.task_note || "Offene Aufgabe"} className="inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-wide text-amber-700 bg-amber-500/15 border border-amber-500/40 rounded px-1.5 py-0.5">
          <Flag className="w-3 h-3" /> Aufgabe
        </span>
      )}
      </div>
    );

  const extraKeys = useMemo(
    () => {
      const fromDevices = devices.flatMap((d) => (d.extra ? Object.keys(d.extra) : []));
      const known = Object.keys(LABELS).filter((k) => k.startsWith("extra.")).map((k) => k.slice(6));
      return Array.from(new Set([...fromDevices, ...known])).sort();
    },
    [devices]
  );

  const allCols = useMemo(() => {
    const base = [
      { key: "agent", label: t("agent_col"), render: (d) => <AgentCell installed={d.agent_installed} /> },
      { key: "actions", label: t("actions"), render: (d) => (
        <div className="flex items-center gap-0.5" onClick={(e) => e.stopPropagation()}>
          <button
            data-testid={`flag-device-${d.device_code}`}
            onClick={() => setFlagDev(d)}
            title={d.task_flag ? (d.task_note || "Markierte Aufgabe") : "Als Aufgabe markieren"}
            className={`p-1.5 rounded transition-colors ${d.task_flag ? "text-amber-600 hover:bg-amber-500/10" : "text-gray-600 hover:text-amber-700 hover:bg-gray-100"}`}
          >
            <Flag className={`w-4 h-4 ${d.task_flag ? "fill-amber-400/30" : ""}`} />
          </button>
          <button
            data-testid={`open-device-${d.device_code}`}
            onClick={() => onOpen(d)}
            className="p-1.5 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded transition-colors"
          >
            <Eye className="w-4 h-4" />
          </button>
        </div>
      ) },
      { key: "asset_tag", label: "AssetID", render: (d) => d.asset_tag
        ? <span className="font-mono text-sm font-bold text-gray-900 bg-gray-50 border border-gray-200 rounded px-2 py-1">{d.asset_tag}</span>
        : dash },
      { key: "device_code", label: t("device_code"), render: renderCode },
      { key: "location_id", label: t("location_id"), render: (d) => <span className="inline-flex items-center gap-1.5"><span className="font-mono text-sm text-amber-700">{d.location_id || "—"}</span>{ticketBadge(d.location_id)}</span> },
      { key: "location_name", label: t("location_name"), render: (d) => d.location_name ? <span className="font-mono text-sm text-gray-700">{d.location_name}</span> : dash },
      { key: "group", label: lang === "de" ? "Gruppe" : "Group", render: (d) => d.group
        ? <span className="text-xs font-mono px-2 py-0.5 rounded bg-gray-100 text-gray-800 border border-gray-300">{d.group}</span>
        : <span className="text-xs text-gray-600 font-mono">{d.effective_group || "—"} <span className="opacity-50">(auto)</span></span> },
      { key: "hostname", label: t("hostname"), render: (d) => <span className="font-mono text-sm text-gray-700">{d.hostname || "—"}</span> },
      { key: "device_uid", label: t("device_uid"), render: (d) => <UidCell value={d.device_uid} /> },
      { key: "teamviewer_id", label: t("teamviewer_id"), render: (d) => <TeamViewerCell id={d.teamviewer_id} uid={d.device_code} status={tvStatusOf(d)} changedAt={d.tv_changed_at} prevId={d.tv_prev_id} /> },
      { key: "tablet_serial", label: t("tablet_serial"), render: (d) => <span className="font-mono text-sm text-gray-600">{d.tablet_serial || "—"}</span> },
      { key: "manufacturer", label: t("manufacturer"), render: (d) => textCell(d.manufacturer) },
      { key: "model", label: t("model"), render: (d) => textCell(d.model) },
      { key: "os_version", label: t("os_version"), render: (d) => textCell(d.os_version) },
      { key: "agent_version", label: t("agent_version"), render: (d) => <span className="font-mono text-sm text-gray-600">{d.agent_version || "—"}</span> },
      { key: "payload_version", label: lang === "de" ? "Skript-Version" : "Script version", render: (d) => {
        if (d.payload_version === undefined || d.payload_version === null || d.payload_version === "") return dash;
        const err = d.payload_status === "error";
        const isDraft = d.payload_channel === "draft";
        const cls = err
          ? "bg-red-500/10 text-red-700 border-red-500/40"
          : isDraft
          ? "bg-amber-500/10 text-amber-700 border-amber-500/40"
          : "bg-gray-100 text-gray-800 border-gray-300";
        return (
          <span
            title={err ? (d.payload_error || "Fehler") : isDraft ? (lang === "de" ? "Entwurf/Test" : "Draft/test") : ""}
            className={`inline-flex items-center gap-1 text-xs font-mono px-2 py-0.5 rounded border ${cls}`}
          >
            {err && <span className="w-1.5 h-1.5 rounded-full bg-red-400" />}v{d.payload_version}{isDraft && !err ? (lang === "de" ? " Test" : " draft") : ""}
          </span>
        );
      } },
      { key: "last_seen", label: t("last_seen"), render: (d) => <span className="text-sm text-gray-600 font-mono whitespace-nowrap">{fmtDate(d.last_seen)}</span> },
      { key: "first_seen", label: t("first_seen"), render: (d) => <span className="text-sm text-gray-600 font-mono whitespace-nowrap">{fmtDate(d.first_seen)}</span> },
    ];
    const dateOnlyExtra = new Set(["devices_dat", "db_dat"]);
    const extra = extraKeys.map((k) => ({
      key: `extra.${k}`,
      label: fieldLabel(`extra.${k}`, t),
      render:
        k === "internet"
          ? (d) => <InternetLed value={(d.extra && d.extra[k]) || ""} />
          : k === "scanner_connected"
          ? (d) => <ScannerLed value={(d.extra && d.extra[k]) || ""} />
          : dateOnlyExtra.has(k)
          ? (d) => <span className="font-mono text-sm text-gray-700 whitespace-nowrap">{(d.extra && d.extra[k]) ? fmtDateOnly(d.extra[k]) : "—"}</span>
          : (d) => <span className="font-mono text-sm text-gray-700">{fmtAuto((d.extra && d.extra[k]) || "") || "—"}</span>,
    }));
    return [...base, ...extra];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [extraKeys, editing, editValue, lang, t]);

  const orderedCols = useMemo(() => {
    const byKey = new Map(allCols.map((c) => [c.key, c]));
    return applyOrder(allCols.map((c) => c.key), order).map((k) => byKey.get(k));
  }, [allCols, order]);

  const orderedKeys = orderedCols.map((c) => c.key);
  const visibleCols = orderedCols.filter((c) => inTable(placement, c.key));

  const [sort, setSort] = useState({ key: null, dir: "asc" });
  const toggleSort = (key) =>
    setSort((s) => (s.key !== key ? { key, dir: "asc" } : s.dir === "asc" ? { key, dir: "desc" } : { key: null, dir: "asc" }));
  const rows = useMemo(() => {
    if (!sort.key) return devices;
    const arr = [...devices];
    arr.sort((a, b) => cmpVals(sortValue(a, sort.key), sortValue(b, sort.key)));
    if (sort.dir === "desc") arr.reverse();
    return arr;
  }, [devices, sort]);

  const SortTh = ({ colKey, label, className }) => (
    <th className={className}>
      <button
        data-testid={`sort-${colKey}`}
        onClick={() => toggleSort(colKey)}
        className="inline-flex items-center gap-1 hover:text-white transition-colors"
      >
        {label}
        {sort.key === colKey ? (
          sort.dir === "asc" ? <ChevronUp className="w-3 h-3 text-white" /> : <ChevronDown className="w-3 h-3 text-white" />
        ) : (
          <ChevronsUpDown className="w-3 h-3 opacity-30" />
        )}
      </button>
    </th>
  );

  return (
    <div className="card-surface rounded-xl">
      {!devices.length ? (
        <div className="p-12 text-center text-gray-600 text-sm" data-testid="empty-devices">
          {t("no_devices")}
        </div>
      ) : (
        <>
          <div className="hidden md:block overflow-auto max-h-[calc(100vh-200px)] rounded-lg border border-gray-100">
          <table className="w-full text-left min-w-max" data-testid="device-table">
            <thead className="sticky top-0 bg-[#1B1E23] z-20">
              <tr className="text-xs font-mono uppercase tracking-wider text-gray-100 border-b border-[#1B1E23]">
                <th className="px-2 py-3 w-8">
                  <input
                    type="checkbox"
                    data-testid="select-all"
                    checked={allSelected}
                    onChange={() => onToggleAll && onToggleAll(devices.map((d) => d.device_code), !allSelected)}
                    className="accent-[#c00000] w-4 h-4 align-middle"
                  />
                </th>
                <th className="px-2 py-3 font-medium w-8 relative">
                  <button
                    data-testid="columns-button"
                    onClick={() => setColMenu(true)}
                    title={t("columns")}
                    className="text-gray-300 hover:text-white p-1 rounded-md border border-white/20 hover:border-white/40 transition-colors"
                  >
                    <Columns3 className="w-3.5 h-3.5" />
                  </button>
                </th>
                {visibleCols.map((c) => (
                  <SortTh key={c.key} colKey={c.key} label={c.label} className="px-2 py-3 font-medium whitespace-nowrap" />
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((d) => (
                <tr
                  key={d.device_code}
                  data-testid={`device-row-${d.device_code}`}
                  onClick={() => onOpen(d)}
                  className={`border-b border-gray-100 odd:bg-white even:bg-gray-50 hover:bg-gray-100 cursor-pointer transition-colors ${flash[d.device_code] ? "flash" : ""} ${has(d.device_code) ? "bg-[#c00000]/[0.06]" : ""}`}
                >
                  <td className={rowPad} onClick={(e) => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      data-testid={`select-${d.device_code}`}
                      checked={has(d.device_code)}
                      onChange={() => onToggleSelect && onToggleSelect(d.device_code)}
                      className="accent-[#c00000] w-4 h-4 align-middle"
                    />
                  </td>
                  <td className={rowPad}><Led status={d.status} /></td>
                  {visibleCols.map((c) => (
                    <td key={c.key} className={rowPad}>{c.render(d)}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
          </div>

          <div className="md:hidden divide-y divide-gray-100" data-testid="device-cards-mobile">
            {rows.map((d) => {
              const scn = (d.extra || {}).scanner_connected;
              const closed = d.closes_on && d.closes_on <= new Date().toISOString().slice(0, 10);
              return (
                <div
                  key={d.device_code}
                  data-testid={`device-card-${d.device_code}`}
                  onClick={() => onOpen(d)}
                  className={`p-3.5 flex flex-col gap-2.5 active:bg-gray-50 transition-colors ${has(d.device_code) ? "bg-[#c00000]/[0.06]" : ""}`}
                >
                  <div className="flex items-center gap-2.5">
                    <input
                      type="checkbox"
                      data-testid={`select-m-${d.device_code}`}
                      checked={has(d.device_code)}
                      onClick={(e) => e.stopPropagation()}
                      onChange={() => onToggleSelect && onToggleSelect(d.device_code)}
                      className="accent-[#c00000] w-4 h-4 shrink-0"
                    />
                    <Led status={d.status} />
                    <span className="font-mono text-sm font-semibold text-gray-800">{d.device_code || "—"}</span>
                    {(d.device_code || "").includes("?") && (
                      <span data-testid={`unclear-badge-m-${d.device_code}`} title={t("unclear_code") || "Unklarer Gerätecode – bitte korrigieren"} className="inline-flex items-center gap-0.5 font-mono text-[9px] uppercase tracking-wide text-amber-700 bg-amber-500/15 border border-amber-500/30 rounded px-1 py-0.5">
                        <AlertTriangle className="w-2.5 h-2.5" /> ?
                      </span>
                    )}
                    {closed && (
                      <span className="font-mono text-[9px] uppercase tracking-wide text-red-700 bg-red-500/15 border border-red-500/30 rounded px-1 py-0.5">closed</span>
                    )}
                    <div className="ml-auto flex items-center gap-2.5 shrink-0">
                      {scn ? <ScannerLed value={scn} /> : null}
                      <AgentCell installed={d.agent_installed} />
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-wrap pl-[26px]">
                    {d.asset_tag && (
                      <span className="font-mono text-xs font-bold text-gray-900 bg-gray-50 border border-gray-200 rounded px-2 py-0.5">{d.asset_tag}</span>
                    )}
                    {d.location_id && <span className="font-mono text-xs text-amber-700">{d.location_id}</span>}
                    {ticketBadge(d.location_id)}
                    {d.location_name && <span className="font-mono text-xs text-gray-600 truncate max-w-[55%]">{d.location_name}</span>}
                    {d.group && (
                      <span className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-gray-100 text-gray-800 border border-gray-300">{d.group}</span>
                    )}
                  </div>
                  <div className="flex items-center justify-between gap-2 pl-[26px]">
                    <div onClick={(e) => e.stopPropagation()} className="min-w-0">
                      {d.teamviewer_id ? (
                        <TeamViewerCell id={d.teamviewer_id} uid={d.device_code} status={tvStatusOf(d)} changedAt={d.tv_changed_at} prevId={d.tv_prev_id} />
                      ) : (
                        <span className="text-xs text-gray-600 font-mono">— TV</span>
                      )}
                    </div>
                    <span className="text-[11px] text-gray-600 font-mono whitespace-nowrap shrink-0">{fmtDate(d.last_seen)}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}

      <Dialog open={colMenu} onOpenChange={setColMenu}>
        <DialogContent data-testid="columns-menu" className="max-w-2xl max-h-[88vh] flex flex-col bg-white border-gray-200 text-gray-900">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-gray-900">
              <Columns3 className="w-5 h-5 text-gray-800" />
              {t("columns")}
            </DialogTitle>
            <DialogDescription className="text-gray-600">
              {lang === "en"
                ? "Drag rows by the handle to reorder. Choose where each field appears: Table, Detail, Both or Off."
                : "Zeilen am Griff ziehen zum Ordnen. Wähle, wo jedes Feld erscheint: Tabelle, Detail, Beides oder Aus."}
            </DialogDescription>
          </DialogHeader>

          <Reorder.Group axis="y" values={orderedKeys} onReorder={reorder} className="space-y-1.5 flex-1 min-h-0 overflow-y-auto pr-1 -mr-1">
            {orderedCols.map((c, idx) => (
              <ColRow
                key={c.key} colKey={c.key} label={c.label}
                cur={placementOf(placement, c.key)}
                opts={[["table", t("place_table")], ["detail", t("place_detail")], ["both", t("place_both")], ["off", t("place_off")]]}
                isFirst={idx === 0} isLast={idx === orderedCols.length - 1} lang={lang}
                onUp={() => moveKey(c.key, -1)} onDown={() => moveKey(c.key, 1)} onPlace={(v) => setPlace(c.key, v)}
              />
            ))}
          </Reorder.Group>

          <div className="shrink-0 flex items-center justify-between pt-2 border-t border-gray-200 text-xs text-gray-600 font-mono">
            <span>{t("place_table")[0]}=Tab · {t("place_detail")[0]}=Det · {t("place_both")[0]}=Both · {t("place_off")[0]}=Off</span>
            <span>{visibleCols.length}/{orderedCols.length} {lang === "en" ? "in table" : "in Tabelle"}</span>
          </div>
        </DialogContent>
      </Dialog>

      <TaskFlagDialog device={flagDev} onClose={() => setFlagDev(null)} onSaved={() => onChanged && onChanged()} de={lang !== "en"} />
    </div>
  );
}

function ColRow({ colKey, label, cur, opts, isFirst, isLast, lang, onUp, onDown, onPlace }) {
  const controls = useDragControls();
  return (
    <Reorder.Item
      value={colKey}
      dragListener={false}
      dragControls={controls}
      data-testid={`col-row-${colKey}`}
      className="flex items-center gap-2 px-3 py-2 rounded-lg border border-gray-200 bg-white/[0.04] transition-colors select-none"
    >
      <button
        type="button"
        onPointerDown={(e) => controls.start(e)}
        data-testid={`col-drag-${colKey}`}
        title={lang === "en" ? "Drag to reorder" : "Ziehen zum Ordnen"}
        className="cursor-grab active:cursor-grabbing touch-none text-gray-600 hover:text-gray-900 p-1 -ml-1 rounded"
      >
        <GripVertical className="w-4 h-4" />
      </button>
      <span className="text-gray-800 truncate flex-1 min-w-0 text-sm" title={label}>{label}</span>
      <button data-testid={`col-up-${colKey}`} disabled={isFirst} onClick={onUp} title={lang === "en" ? "Move up" : "Nach oben"}
        className="p-1.5 rounded text-gray-600 hover:text-gray-900 hover:bg-gray-100 disabled:opacity-25 disabled:hover:bg-transparent transition-colors">
        <ChevronUp className="w-4 h-4" />
      </button>
      <button data-testid={`col-down-${colKey}`} disabled={isLast} onClick={onDown} title={lang === "en" ? "Move down" : "Nach unten"}
        className="p-1.5 rounded text-gray-600 hover:text-gray-900 hover:bg-gray-100 disabled:opacity-25 disabled:hover:bg-transparent transition-colors">
        <ChevronDown className="w-4 h-4" />
      </button>
      <div className="flex rounded-md overflow-hidden border border-gray-200 shrink-0" data-testid={`col-place-${colKey}`}>
        {opts.map(([val, lbl]) => (
          <button
            key={val}
            data-testid={`col-place-${colKey}-${val}`}
            onClick={() => onPlace(val)}
            title={lbl}
            className={`px-2 py-1 text-[11px] font-mono transition-colors ${cur === val ? "bg-[#c00000] text-white font-bold" : "bg-transparent text-gray-600 hover:bg-gray-100"}`}
          >
            {lbl[0]}
          </button>
        ))}
      </div>
    </Reorder.Item>
  );
}
